import React, { ErrorInfo } from "react";

export interface IErrorBoundaryProps {
  /**
   * This was built of off `react-error-boundary`, for more in-depth API documentation:
   * @see https://github.com/bvaughn/react-error-boundary#api
   */

  /** This is called when `resetKeys` are changed (triggering a reset of the `ErrorBoundary`). This is called with `prevResetKeys` and `resetKeys`.  */
  onResetKeysChange?: (prevResetKeys: Array<unknown> | undefined, resetKeys: Array<unknown> | undefined) => void;

  /**
   * This will be called immediately before the ErrorBoundary resets it's internal state (which will result in rendering the children again).
   * You should use this to ensure that re-rendering the children will not result in a repeat of the same error happening again.
   * `onReset` will be called with whatever `resetErrorBoundary` is called with.
   * Important: onReset will not be called when reset happens from a change in resetKeys. Use onResetKeysChange for that.
   */
  onReset?: (...args: Array<unknown>) => void;
  /**
   * This will be called when there's been an error that the ErrorBoundary has handled. It will be called with two arguments: `error`, `info`.
   */
  onError?: (error: Error, info: { componentStack: string }) => void;

  /**
   * Sometimes an error happens as a result of local state to the component that's rendering the error.
   * If this is the case, then you can pass resetKeys which is an array of values.
   * If the ErrorBoundary is in an error state, then it will check these values each render
   * and if they change from one render to the next, then it will reset automatically (triggering a re-render of the children).
   */
  resetKeys?: Array<unknown>;

  /** if `true`, error message will be rendered within modal component */
  useModalState?: boolean;
}

export interface IErrorBoundaryBaseLogicWithComponent extends IErrorBoundaryProps {
  renderComponent: (props: IState) => React.ReactNode;
}

export interface IState {
  error: Error | null;
  info: ErrorInfo | null;
  resetErrorBoundary: ((...args: unknown[]) => void) | null;
}

export interface ErrorBoundaryState {
  error: Error | null;
  info: React.ErrorInfo | null;
}

export const initialState: ErrorBoundaryState = { error: null, info: null };

export const changedArray = (a: Array<unknown> = [], b: Array<unknown> = []) =>
  a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]));

/**
 * React's error boundaries feature is limited in that the boundaries can only handle errors thrown during React's lifecycles.
 * Use `useErrorHandler` in cases where you need to manage error handling yourself (ie. from within event handlers or API calls )
 * @see https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries
 * @see https://github.com/bvaughn/react-error-boundary#useerrorhandlererror-unknown
 */
export function useErrorHandler(givenError?: unknown): (error: unknown) => void {
  const [error, setError] = React.useState<unknown>(null);
  if (givenError != null) {
    throw givenError;
  }
  if (error != null) {
    throw error;
  }
  return setError;
}

/**
 * ErrorBoundary must be constructed as a class component
 * @see https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries
 */
export class ErrorBoundaryBaseLogic extends React.Component<
  React.PropsWithRef<React.PropsWithChildren<IErrorBoundaryBaseLogicWithComponent>>,
  ErrorBoundaryState,
  IState
> {
  constructor(props: IErrorBoundaryBaseLogicWithComponent) {
    super(props);
    this.state = initialState;
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  resetErrorBoundary = (...args: Array<unknown>) => {
    this.props.onReset?.(...args);
    this.reset();
  };

  reset() {
    this.setState(initialState);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const safeInfo = { componentStack: info.componentStack || "" };
    this.props.onError?.(error, safeInfo);
    this.setState({ error, info });
  }

  componentDidUpdate(prevProps: IErrorBoundaryBaseLogicWithComponent, prevState: ErrorBoundaryState) {
    const { error } = this.state;
    const { resetKeys } = this.props;

    // There's an edge case where if the thing that triggered the error
    // happens to *also* be in the resetKeys array, we'd end up resetting
    // the error boundary immediately. This would likely trigger a second
    // error to be thrown.
    // So we make sure that we don't check the resetKeys on the first call
    // of cDU after the error is set

    if (error !== null && prevState.error !== null && changedArray(prevProps.resetKeys, resetKeys)) {
      this.props.onResetKeysChange?.(prevProps.resetKeys, resetKeys);
      this.reset();
    }
  }

  render() {
    const { error } = this.state;
    const props = {
      error: this.state.error ?? null,
      info: this.state.info ?? null,
      resetErrorBoundary: this.props.onReset ? this.resetErrorBoundary : null,
    };
    if (error !== null) {
      return this.props.renderComponent(props);
    }
    return this.props.children;
  }
}
